"use client";
import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Button from "../ui/Button";
import ContactModal from "../Home/ContactModal";
import FinanceablePill from "./FinanceablePill";
import useMediaQuery from "@/hooks/useMediaQuery";
import type { IFormationDetail } from "@/types/formation";

interface FicheStickyCtaProps {
  formation: IFormationDetail;
}

/* The two bottom-bar CTAs. min-h keeps them a 44px target even when only one of
   the two labels wraps to a second line. */
const CTA_CLASS =
  "flex-1 min-h-11 rounded-lg text-[12px] leading-4 text-center px-2.5 py-3";

/**
 * Mobile-only action bar pinned to the bottom of the fiche: the two CTAs stay
 * within thumb reach the whole way down the page, where the hero's price card
 * has long scrolled away. It also carries the category / financeable /
 * reference trio, which the hero only shows from `sm` up.
 */
const FicheStickyCta: React.FC<FicheStickyCtaProps> = ({ formation }) => {
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const [isContactOpen, setIsContactOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  /* The bar floats over the page, so the footer's last rows would end up
     underneath it — hand the body back exactly the bar's height. */
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;

    const applyOffset = () => {
      document.body.style.paddingBottom = `${bar.offsetHeight}px`;
    };

    applyOffset();
    window.addEventListener("resize", applyOffset);
    return () => {
      window.removeEventListener("resize", applyOffset);
      document.body.style.paddingBottom = "";
    };
  }, [isMobile]);

  /* Desktop and large tablets keep the price card in the rail, sticky already. */
  if (!isMobile) return null;

  const isFinanceable = formation.is_financeable;

  return (
    <>
      <div
        ref={barRef}
        /* env(safe-area-inset-bottom) clears the iOS home indicator. */
        style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }}
        className="fixed inset-x-0 bottom-0 z-40 bg-white/35 backdrop-blur-md shadow-[0_-2px_8px_2px_#0000001A] px-4 pt-2.5"
      >
        <div className="flex items-center gap-2 pb-2.5 overflow-x-auto no-scrollbar">
          <Link
            href={`/formations/${formation.category_slug}`}
            className="shrink-0 rounded-full bg-linear-to-b from-[#FFBC41] to-[#FF5758] text-white text-[11px] font-bold leading-5 px-2.5 py-1 hover:opacity-90 transition-opacity"
          >
            {formation.category}
          </Link>
          {isFinanceable && <FinanceablePill className="shrink-0" />}
          {formation.reference && (
            <span className="shrink-0 text-[11px] leading-5 text-[#1B365E]">
              {formation.reference}
            </span>
          )}
        </div>

        <div className="flex items-stretch gap-2.5">
          <Button
            variant="gradient"
            size="unset"
            onClick={() => setIsContactOpen(true)}
            className={CTA_CLASS}
          >
            Discutez de la formation
          </Button>

          {/* Was a Button nested in a Link — one anchor does both jobs now. */}
          <Button
            href="#sessions"
            variant="primary"
            size="unset"
            className={CTA_CLASS}
          >
            Consulter les dates et lieux
          </Button>
        </div>
      </div>

      <ContactModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
        origin="formation"
        formationTitle={formation.title}
        formationTheme={formation.category}
        message={`Demande d'échange au sujet de la formation « ${formation.title} ».`}
      />
    </>
  );
};

export default FicheStickyCta;
